import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Label } from "@progress/kendo-react-labels";
import { Button } from "@progress/kendo-react-buttons";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { LoadingPanel } from "../../LoadingPanel";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { filterBy } from "@progress/kendo-data-query";
import { Dialog } from "@progress/kendo-react-dialogs";
import { AdministratorVenueNames } from "./VenueNames";
import { AdministratorMatchVenue } from "./MatchVenue";
import { AdministratorVenuePOSNotes } from "./VenuePOSNotes";
import { AdministratorVenueBlockedSecondaryTicketingWebsites } from "./VenueBlockedSecondaryTicketingWebsites";

import {
    FormInput,
    FormCheckbox,
    FormDropDownList,
    FormMultiColumnComboBox,
    FormNumericTextBox,
} from "../../forms/form-components";

import './Venue.scss';

export const AdministratorVenue = (props) => {
    const dispatch = useDispatch();

    const regionComboBoxColumns = [ 
        {
            field: 'name',
            header: "Region",
            width: '200px'
        }, 
        {
            field: 'countryName',
            header: "Country",
            width: '200px',
        }
    ];

    const [regionFilter, setRegionFilter] = useState(undefined);
    const [selectedTab, setSelectedTab] = useState(0);
    const [showDialog, setShowDialog] = useState(false);

    const oneHundredCharacterValidator = (value) => {
        if (!value) {
            return "";
        }

        if (value.length > 100) {
            return "Maximum length is 100 characters";
        }

        return "";
    }

    const oneHundredCharacterRequiredValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        if (value.length > 100) {
            return "Maximum length is 100 characters";
        }

        return "";
    }

    const fiftyCharacterValidator = (value) => {
        if (!value) {
            return "";
        }

        if (value.length > 50) {
            return "Maximum length is 50 characters";
        }

        return "";
    }

    const twoHundredCharacterValidator = (value) => {
        if (!value) {
            return "";
        }

        if (value.length > 200) {
            return "Maximum length is 200 characters";
        }

        return "";
    }

    const requiredValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        return "";
    }

    const handleSubmit = (dataItem) => {
        let fetchMethod, successMessage;

        if (!dataItem.venueKey) {
            //Create new venue
            fetchMethod = "POST";
            successMessage = "Created item successfully";
        }
        else {
            //Update existing venue
            fetchMethod = "PUT";
            successMessage = "Updated item successfully";
        }

        if (dataItem.region) {
            if (dataItem.region.regionIsoCode === "") {
                dataItem.regionCode = null;
            }
            else {
                dataItem.regionCode = dataItem.region.regionIsoCode;
            }
        }

        if (dataItem.country) {
            dataItem.countryCode = dataItem.country.twoLetterIsoCode;
        }

        //Perform validation
        if (dataItem.regionCode) {
            const region = props.regions.find((p) => p.regionIsoCode === dataItem.regionCode && p.twoLetterIsoCountryCode === dataItem.countryCode);

            if (!region) {
                addNotificationWithTimeout(dispatch, "error", "The selected region isn't located in the selected country");
                return;
            }
        }

        fetch("/api/administrator/venue",
            {
                method: fetchMethod,
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", successMessage);

                props.selectVenue(result.venueKey);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const deleteVenue = (venueKey) => {
        if (window.confirm("Are you sure you want to delete this item?") === false) {
            return;
        }

        const dataItem = {
            "venueKey": venueKey
        }

        fetch("/api/administrator/venue",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                props.cancel();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const savePOSVenueId = (posVenueId) => {
        const dataItem = {
            posVenueId: posVenueId,
            venueKey: props.venue.venueKey,
        };

        fetch("/api/administrator/pos/venue",
            {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setShowDialog(false);

                addNotificationWithTimeout(dispatch, "success", "Saved POS Venue Id successfully");

                props.selectVenue(props.venue.venueKey);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    const tabStripSelectionChanged = (e) => {
        setSelectedTab(e.selected);
    };

    const getForm = (venue) => {
        if (!venue) {
            venue = {
                isActive: true,
                createAlertsForNewEvents: false,
                ticketPriceIncludesTaxesAndFees: false,
                country: props.countries.find(element => element.twoLetterIsoCode === "US"),
            }
        }
        else {
            venue.region = props.regions.find(element => element.regionIsoCode === venue.regionCode && element.twoLetterIsoCountryCode === venue.countryCode);
            venue.country = props.countries.find(element => element.twoLetterIsoCode === venue.countryCode);
        }

        if (!venue?.venueKey && selectedTab > 0) {
            //Cannot have selected tab be a disabled one
            setSelectedTab(0);
        }

        return (
            <div className="administratorVenue">
                <TabStrip selected={selectedTab} onSelect={tabStripSelectionChanged}>
                    <TabStripTab title={"Venue"}>
                        <Form
                            onSubmit={handleSubmit}
                            initialValues={venue}
                            key={JSON.stringify(venue)}
                            render={(formRenderProps) => (
                                <FormElement>
                                    <h2>
                                        {!venue?.venueKey ? "New Venue" : "Update Venue"}
                                    </h2>
                                    
                                    <fieldset className={"k-form-fieldset"}>
                                        {!venue?.venueKey ? "" :
                                            (<div className="mb-3 noFormField">
                                                <Label>Key:</Label> {venue.venueKey}
                                            </div>)
                                        }

                                        <Field
                                            id={"isActive"}
                                            name={"isActive"}
                                            label={"Active"}
                                            component={FormCheckbox}
                                        />

                                        <Field
                                            id={"createAlertsForNewEvents"}
                                            name={"createAlertsForNewEvents"}
                                            label={"Create Alerts for New Events"}
                                            component={FormCheckbox}
                                        />

                                        <Field
                                            id={"ticketPriceIncludesTaxesAndFees"}
                                            name={"ticketPriceIncludesTaxesAndFees"}
                                            label={"Ticket Price Includes Taxes and Fees"}
                                            component={FormCheckbox}
                                        />

                                        <Field
                                            id={"currentName"}
                                            name={"currentName"}
                                            label={"Current name"}
                                            component={FormInput}
                                            validator={oneHundredCharacterRequiredValidator}
                                        />

                                        <div className="row">
                                            <div className="col-md-6">
                                                <Field
                                                    id={"address1"}
                                                    name={"address1"}
                                                    label={"Address 1"}
                                                    component={FormInput}
                                                    validator={oneHundredCharacterRequiredValidator}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <Field
                                                    id={"address2"}
                                                    name={"address2"}
                                                    label={"Address 2"}
                                                    component={FormInput}
                                                    validator={oneHundredCharacterValidator}
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Field
                                                    id={"city"}
                                                    name={"city"}
                                                    label={"City"}
                                                    component={FormInput}
                                                    validator={oneHundredCharacterRequiredValidator}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <Field
                                                    id={"region"}
                                                    name={"region"}
                                                    label={"Region"}
                                                    component={FormMultiColumnComboBox}
                                                    data={regionFilter ? filterBy(props.regions, regionFilter) : props.regions}
                                                    textField="name"
                                                    dataItemKey="regionIsoCode"
                                                    groupField="countryName"
                                                    columns={regionComboBoxColumns}
                                                    suggest={true}
                                                    onFilterChange={(event) => setRegionFilter(event.filter)}
                                                    onChange={() => setRegionFilter(undefined)}
                                                    filterable={true}  
                                                    autoComplete="doNotShow"
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Field
                                                    id={"postalCode"}
                                                    name={"postalCode"}
                                                    label={"Postal code"}
                                                    component={FormInput}
                                                    validator={fiftyCharacterValidator}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <Field
                                                    id={"country"}
                                                    name={"country"}
                                                    label={"Country"}
                                                    component={FormDropDownList}
                                                    data={props.countries}
                                                    textField="name"
                                                    dataItemKey="twoLetterIsoCode"
                                                    validator={requiredValidator}
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Field
                                                    id={"orderFee"}
                                                    name={"orderFee"}
                                                    label={"Order fee"}
                                                    component={FormNumericTextBox}
                                                    spinners={false}
                                                    format="n2"
                                                />                                                
                                            </div>
                                            <div className="col-md-6">
                                                <Field
                                                    id={"taxPercentage"}
                                                    name={"taxPercentage"}
                                                    label={"Tax percentage"}
                                                    component={FormNumericTextBox}
                                                    spinners={false}
                                                    format="n2"
                                                /> 
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Field
                                                    id={"ticketCountForNewEventAlerts"}
                                                    name={"ticketCountForNewEventAlerts"}
                                                    label={"Ticket count for new event alerts"}
                                                    component={FormNumericTextBox}
                                                    spinners={false}
                                                    format="n0"
                                                />                                                
                                            </div>
                                            <div className="col-md-4">
                                                <Field
                                                    id={"posVenueId"}
                                                    name={"posVenueId"}
                                                    label={"POS Venue Id"}
                                                    component={FormInput}
                                                    validator={fiftyCharacterValidator}
                                                />                                                
                                            </div>
                                            <div className="col-md-2">
                                                <div className="k-form-field">
                                                    <Label className="k-label">&nbsp;</Label>

                                                    {!venue?.venueKey ? null :
                                                        (<Button
                                                            type={"button"}
                                                            onClick={() => setShowDialog(true)}>
                                                            Match
                                                        </Button>
                                                    )}                                                    
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Field
                                                    id={"defaultShortingEventTicketTypeName"}
                                                    name={"defaultShortingEventTicketTypeName"}
                                                    label={"Default shorting ticket type name"}
                                                    component={FormInput}
                                                    validator={twoHundredCharacterValidator}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <Field
                                                    id={"defaultMinimumAge"}
                                                    name={"defaultMinimumAge"}
                                                    label={"Default minimum age"}
                                                    component={FormNumericTextBox}
                                                    spinners={false}
                                                    format="n0"
                                                />
                                            </div>
                                        </div>
                                    </fieldset>
                                    <span className={"k-form-separator"} />
                                    <div className="k-form-buttons">
                                        <Button
                                            themeColor={"primary"}
                                            type={"submit"}
                                            disabled={!formRenderProps.allowSubmit}
                                            >
                                            Save
                                        </Button>

                                        {!venue?.venueKey ? null :
                                            (<Button
                                                type={"button"}
                                                className={"deleteButton"}
                                                onClick={() => deleteVenue(venue.venueKey)}>
                                                Delete
                                            </Button>
                                        )}
                                    </div>
                                </FormElement>
                            )}
                        />
                    </TabStripTab>
                    <TabStripTab title={"Alternate names"} disabled={!venue?.venueKey}>
                        <AdministratorVenueNames venueKey={venue?.venueKey} />
                    </TabStripTab>
                    <TabStripTab title={"POS notes"} disabled={!venue?.venueKey}>
                        <AdministratorVenuePOSNotes venueKey={venue?.venueKey} />
                    </TabStripTab>
                    <TabStripTab title={"Blocked Websites"} disabled={!venue?.venueKey}>
                        <AdministratorVenueBlockedSecondaryTicketingWebsites venueKey={venue?.venueKey} />
                    </TabStripTab>
                </TabStrip>
                <div className="administratorVenueClose">
                    <Button
                        themeColor={"primary"}
                        type={"button"}
                        onClick={props.cancel}
                    >
                        Close
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <>
            { props.loading === true ? <LoadingPanel /> : (props.createNewVenue === true ? getForm() : (props.venue ? getForm(props.venue) : <div></div>))}

            {showDialog === false ? <></> :
                <Dialog
                    title="Match Venues"
                    onClose={() => setShowDialog(false)}
                    width={"90vw"}
                >
                    <AdministratorMatchVenue 
                        venueKey={props.venue?.venueKey} 
                        venueName={props.venue?.currentName} 
                        venueCity={props.venue?.city}
                        venueRegionCode={props.venue?.regionCode}
                        venuePostalCode={props.venue?.postalCode}
                        venueCountryCode={props.venue?.countryCode}
                        savePOSVenueId={savePOSVenueId} />
                </Dialog>
            }
        </>
    );
}